// @flow
import * as React from 'react';
import idx from 'idx';
import { graphql, compose } from 'react-apollo';

import GET_AUTO_MESSAGES_FOR_ORG_QUERY from './queries/GetAutoMessagesForOrg.gql';
import AUTO_MESSAGE_SENDS_SUBSCRIPTION_NO_DETAILS from './queries/AutoMessageSendsSubscriptionNoDetails.gql';
import SAVE_AUTO_MESSAGE_MUTATION from 'admin/components/AdminNewAutoMessage/queries/SaveAutoMessageMutation.gql';

import type {
  AdminAutoMessages,
  AdminAutoMessages_organizations,
  SaveAutoMessageInput,
} from './queries/__generated__/AdminAutoMessages';

type Props = {
  children: (props: {
    loading: ?boolean,
    organization: ?AdminAutoMessages_organizations,
    saveAutoMessage: (input: SaveAutoMessageInput) => Promise<*>,
  }) => React.Node,
  data: ReactApolloData<AdminAutoMessages>,
  viewingOrgUuid: string,
  save: (input: SaveAutoMessageInput, viewingOrgUuid: string) => Promise<*>,
};

class AdminAutoMessagesContainer extends React.Component<Props, void> {
  componentDidMount = () => {
    this.subscribeToSends();
  };

  componentDidUpdate = () => {
    this.subscribeToSends();
  };

  subscribeToSends = () => {
    const { data } = this.props;
    const refetchData = idx(data, _ => _.refetch) || (() => {});
    const currentOrg = data.organizations ? data.organizations[0] : null;

    if (currentOrg && currentOrg.autoMessages) {
      const messageUuids = currentOrg.autoMessages
        .filter((msg: any) => msg.status === 'active')
        .map((msg: any) => msg.uuid);

      data.subscribeToMore({
        document: AUTO_MESSAGE_SENDS_SUBSCRIPTION_NO_DETAILS,
        variables: { messageUuids },
        updateQuery: () => {
          // This refetches data for all the auto-messages, not just the one with the send
          // A future improvement would be to only refetch for the automessage we were notified about
          refetchData();
        },
      });
    }
  };

  render() {
    const { children, save, data, viewingOrgUuid } = this.props;

    return children({
      loading: idx(data, _ => _.loading),
      organization: idx(data, _ => _.organizations[0]),
      saveAutoMessage: input => save(input, viewingOrgUuid),
      viewingOrgUuid,
    });
  }
}

const withAutoMessages = graphql(GET_AUTO_MESSAGES_FOR_ORG_QUERY, {
  options: ({ viewingOrgUuid }) => ({
    variables: { organizationUUID: viewingOrgUuid },
    errorPolicy: 'all',
  }),
});

const withSaveMutation = graphql(SAVE_AUTO_MESSAGE_MUTATION, {
  props: ({ mutate }) => ({
    save(input: SaveAutoMessageInput, viewingOrgUuid: string) {
      return mutate({
        variables: { input },
        refetchQueries: [
          {
            query: GET_AUTO_MESSAGES_FOR_ORG_QUERY,
            variables: { organizationUUID: viewingOrgUuid },
          },
        ],
      });
    },
  }),
});

export { AdminAutoMessagesContainer };

export default compose(withAutoMessages, withSaveMutation)(AdminAutoMessagesContainer);
